import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { trpc } from '@/index';

/**
 * OAuth callback — the marketplace redirects here after seller approval.
 *
 * URL params:
 *   ?marketplace=amazon&code=xxx&state=yyy
 *   (Shopify also includes ?shop=xxx.myshopify.com)
 */
export const Route = createFileRoute('/oauth/callback')({
  component: OAuthCallbackPage,
});

function OAuthCallbackPage() {
  const navigate = useNavigate();
  const search = Route.useSearch() as {
    marketplace?: string;
    code?: string;
    state?: string;
    shop?: string;
    error?: string;
    error_description?: string;
  };
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const handleCallback = trpc.shop.handleCallback.useMutation();

  useEffect(() => {
    if (search.error) {
      setStatus('error');
      setErrorMsg(search.error_description ?? search.error);
      return;
    }
    if (!search.marketplace || !search.code || !search.state) {
      setStatus('error');
      setErrorMsg('Missing required OAuth parameters');
      return;
    }

    handleCallback.mutate(
      {
        marketplaceType: search.marketplace as 'amazon' | 'shopify' | 'tiktok_shop',
        code: search.code,
        state: search.state,
        shop: search.shop,
      },
      {
        onSuccess: () => {
          setStatus('success');
          setTimeout(() => navigate({ to: '/shops' }), 1200);
        },
        onError: (err) => {
          setStatus('error');
          setErrorMsg(err.message);
        },
      },
    );
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="text-center space-y-4">
        {status === 'loading' && (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">Completing authorization…</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
            <p>Shop connected successfully! Redirecting…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <p className="font-medium">Authorization failed</p>
            <p className="text-sm text-muted-foreground">{errorMsg}</p>
            <button
              onClick={() => navigate({ to: '/shops/new' })}
              className="text-sm underline text-primary"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
